import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import { parseApiErrorMessage } from '@/utils/parseApiErrorMessage'
import * as Sentry from '@sentry/react'

export type ResourceScopeType = 'order' | 'user'

export type ResourceScopeStatus = 'in_scope' | 'not_found'

/**
 * Thrown when GET /auth/resource-scope/* returns an unexpected status (401, 422, 500, etc.).
 */
export class ResourceScopeProbeError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ResourceScopeProbeError'
    this.status = status
  }
}

/**
 * Lightweight org-scope probe before loading a resource UI or calling the full resource API.
 *
 * @returns `in_scope` on 204, `not_found` on 404 (existence and org scope are intentionally indistinguishable).
 * @throws {ResourceScopeProbeError} for 401, 422, 500, and other non-success statuses.
 */
export async function checkResourceScope(
  resourceType: ResourceScopeType,
  resourceId: string
): Promise<ResourceScopeStatus> {
  return Sentry.startSpan(
    {
      op: 'http.client',
      name: `GET /auth/resource-scope/${resourceType}/${resourceId}`,
    },
    async (span) => {
      span.setAttribute('resource.type', resourceType)
      span.setAttribute('resource.id', resourceId)

      const client = ProviderAPIClient.getInstance()
      const encodedId = encodeURIComponent(resourceId)
      const response = await client.get(`/auth/resource-scope/${resourceType}/${encodedId}`)

      span.setAttribute('http.status_code', response.status)

      if (response.status === 204) {
        return 'in_scope'
      }

      if (response.status === 404) {
        return 'not_found'
      }

      const message = await parseApiErrorMessage(response)
      const error = new ResourceScopeProbeError(
        response.status,
        message || `resource-scope probe failed: ${response.status}`
      )

      span.setAttribute('error', true)
      Sentry.captureException(error, {
        level: response.status >= 500 ? 'error' : 'warning',
        tags: {
          operation: 'auth.resource-scope',
          resource_type: resourceType,
          status: String(response.status),
        },
      })
      throw error
    }
  )
}

/** Convenience wrapper — `true` when the probe returns 204. */
export async function isResourceInScope(resourceType: ResourceScopeType, resourceId: string): Promise<boolean> {
  return (await checkResourceScope(resourceType, resourceId)) === 'in_scope'
}
